import React from 'react';

import { Col, DatePicker, Input, Row, Select, Switch } from 'antd';
import moment from 'moment';
import { useIntl } from 'react-intl';

import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import {
  buildHolidaysListFilterSetValueProps,
  HOLIDAYS_LIST_FILTER_FIELDS,
} from '@src/packages/holidays/holidaysListFilterConfig';
import countries from '@src/util/countries.json';

interface CountryOption {
  name: { common: string };
  spanishName: string;
}

interface HolidayFiltersProps {
  handleFilters: (props: SetValueProps) => void;
  filters: FilterValues;
}

const HolidayFilters: React.FC<HolidayFiltersProps> = ({ handleFilters, filters }) => {
  const intl = useIntl();
  const countryList = countries as CountryOption[];

  const rawDate = filters[HOLIDAYS_LIST_FILTER_FIELDS.DATE]?.value;
  const dateValue = rawDate ? moment(rawDate as string) : null;

  const rawActive = filters[HOLIDAYS_LIST_FILTER_FIELDS.ACTIVE]?.value;
  const activeChecked = typeof rawActive === 'boolean' ? rawActive : false;

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Input
          placeholder={intl.formatMessage({ id: 'holiday.filters.namePlaceholder' })}
          value={String(filters[HOLIDAYS_LIST_FILTER_FIELDS.NAME]?.value ?? '')}
          onChange={e =>
            handleFilters(
              buildHolidaysListFilterSetValueProps(
                HOLIDAYS_LIST_FILTER_FIELDS.NAME,
                e.target.value,
                intl,
              ),
            )
          }
          allowClear
          id={HOLIDAYS_LIST_FILTER_FIELDS.NAME}
        />
      </Col>
      <Col span={24}>
        <DatePicker
          className="gx-w-100"
          placeholder={intl.formatMessage({ id: 'holiday.filters.datePlaceholder' })}
          value={dateValue}
          onChange={date =>
            handleFilters(
              buildHolidaysListFilterSetValueProps(HOLIDAYS_LIST_FILTER_FIELDS.DATE, date, intl),
            )
          }
          format="DD/MM/YYYY"
          allowClear
          id={HOLIDAYS_LIST_FILTER_FIELDS.DATE}
        />
      </Col>
      <Col span={24}>
        <Select
          className="gx-w-100"
          placeholder={intl.formatMessage({ id: 'holiday.filters.countryPlaceholder' })}
          value={String(filters[HOLIDAYS_LIST_FILTER_FIELDS.COUNTRY]?.value ?? '') || undefined}
          onChange={(country, option) => {
            const selectedLabel =
              country && option && !Array.isArray(option) ? String(option.children ?? '') : '';
            handleFilters(
              buildHolidaysListFilterSetValueProps(
                HOLIDAYS_LIST_FILTER_FIELDS.COUNTRY,
                country ?? '',
                intl,
                selectedLabel,
              ),
            );
          }}
          allowClear
          showSearch
          optionFilterProp="children"
          id={HOLIDAYS_LIST_FILTER_FIELDS.COUNTRY}
        >
          {countryList.map(country => (
            <Select.Option key={country.name.common} value={country.name.common}>
              {country.spanishName}
            </Select.Option>
          ))}
        </Select>
      </Col>
      <Col span={24}>
        <div className="gx-mt-2">
          <Switch
            checked={activeChecked}
            onChange={checked =>
              handleFilters(
                buildHolidaysListFilterSetValueProps(
                  HOLIDAYS_LIST_FILTER_FIELDS.ACTIVE,
                  checked,
                  intl,
                ),
              )
            }
            checkedChildren={intl.formatMessage({ id: 'general.yes' })}
            unCheckedChildren={intl.formatMessage({ id: 'general.no' })}
          />
        </div>
      </Col>
    </Row>
  );
};

export default React.memo(HolidayFilters);
